import type { RouteLocationNormalized } from 'vue-router'
import { projectLandingPath } from '#core/utils/project-paths'
import { useQuery } from '@pinia/colada'
import { defineStore } from 'pinia'

/**
 * Temporary store for better DX while project context isn't in the URL.
 * Will be removed once projectId is passed via the route.
 */
export const useAdvisor = defineStore('advisor', () => {
  const route = useRoute()
  const auth = useAuth()
  const projectEditors = useProjectEditors()
  const logger = useDevLogger('useAdvisor')
  const supabase = useSupabaseClient()

  const isPreTge = ref<boolean>(true)
  const isCreateEngagementOpen = ref<boolean>(false)

  // Use auth.project directly instead of local state
  const project = computed(() => auth.project)
  const isInProject = computed(() => route.path.startsWith('/project/'))
  const hasProjectEditControl = computed(() => {
    if (isInPortal.value) {
      return false
    }
    if (auth.isAdvisor && !!projectEditors.canRequestEdit) {
      return true
    }

    return (auth.isOrganizationOwner || projectEditors.canRequestEdit) && auth.project?.isAdvised
  })

  const { data: metrics, isLoading: loadingMetrics, refetch: refreshMetrics } = useQuery({
    ...buildApiQuery('/advisor/dashboard-metrics', { method: 'get' }),
    enabled: () => false,
  })

  const { data: advisorEngagementsDataSource, isPending: loadingEngagements, refetch: refreshEngagements } = useQuery({
    ...buildApiQuery('/advisor/engagements', {
      method: 'get',
      params: () => ({
        query: {
          isPreTge: String(isPreTge.value),
        },
      }),
    }),
    enabled: () => false,
  })

  // Routes an advisor may open without a selected project (see advisor-guard middleware).
  const advisorWhitelistRouteNames = [
    'market-maker-leaderboard',
    'research',
    'research-tokenomics',
    'research-exchange-and-listing',
    'project-projectId-settings',
    'support',
    'project-projectId-academy-slug',
    'share-ticker-projectId-public',
    'onboarding',
    'token-unlocks',
    'universal-token-rating',
    // Every marketing page in the site layer lives under /about.
    'about-',
    'consultation',
  ]

  const isInPortal = computed(() => {
    if (route.path.startsWith('/advisor-portal')) {
      return true
    }
    if (isInProject.value) {
      // Deep-linked into a project URL without a selected project (whitelisted
      // routes admit this) — there is no project context to render, stay portal.
      if (!project.value) {
        return true
      }
      return advisorWhitelistRouteNames
        .filter(name => name.startsWith('project-projectId'))
        .some(name => route.name?.toString().includes(name))
    }
    return isAdvisorWhitelistRouteName(route)
  })

  const isAdvisorDefault = computed(() => auth.isAdvisor && (!isInProject.value || isInPortal.value))

  function isAdvisorWhitelistRouteName(to: RouteLocationNormalized) {
    return advisorWhitelistRouteNames.some(p => to.name?.toString().includes(p))
  }

  const organization = computed(() => {
    const organization = auth.organizations?.find(o => o.type === 'advisor' && o.memberStatus === 'active')
    if (!organization) {
      throw new Error('Advisor organization is required')
    }
    return organization
  })

  // Determines whether the user is the owner of the advisor’s organization.
  const isOrganizationOwner = computed(() => auth.me?.id === organization.value.ownerUserId)

  async function setProject(val: { id: string, name: string }, url?: string) {
    logger.debug('[setProject]', { id: val.id, name: val.name })
    await navigateTo(url || projectLandingPath(val.id), { external: true })
  }

  async function exitProject() {
    logger.debug('[exitProject] redirecting to /advisor-portal')
    await navigateTo('/advisor-portal')
  }

  function reset() {
    auth.setSelectedProject(null)
  }

  async function refreshData() {
    await supabase.auth.refreshSession()

    await Promise.all([
      refreshEngagements(),
      auth.refresh({ from: 'advisor-portal:create-engagement' }),
      refreshMetrics(),
    ])
  }

  watch(isPreTge, () => {
    refreshEngagements()
  })

  // Removed problematic watcher that was causing circular dependency
  // Project state is now managed entirely by the auth system

  return {
    isCreateEngagementOpen,
    isInPortal,
    isInProject,
    isAdvisorDefault,
    isPreTge,
    hasProjectEditControl,
    organization,
    isOrganizationOwner,
    project, // Now computed from auth.project
    isAdvisorWhitelistRouteName,
    exitProject,
    setProject,
    reset,

    metrics,
    loadingMetrics,
    advisorEngagementsDataSource,
    loadingEngagements,

    refreshEngagements,
    refreshMetrics,
    refreshData,
  }
}, {
  persist: {
    // Project persistence is now handled by the auth system
    pick: [],
  },
})
